/**
 * Renewal Shared Theme Constants
 *
 * 이 파일은 shared 컴포넌트에서 사용할 테마 관련 상수들을 정의합니다.
 * MUI 테마와 Figma 디자인 시스템을 기반으로 구성되었습니다.
 */

import { COLOR_CONSTANTS } from "./color";
import { TYPOGRAPHY } from "./typography";
import { SPACING_CONSTANTS } from "./spacing";

// Theme Mode
export const THEME_MODES = {
  light: "light",
  dark: "dark",
} as const;

// Theme Direction
export const THEME_DIRECTIONS = {
  ltr: "ltr",
  rtl: "rtl",
} as const;

// Theme Palette Mode
export const PALETTE_MODES = {
  light: "light",
  dark: "dark",
} as const;

// Theme Transitions
export const THEME_TRANSITIONS = {
  easing: {
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
  },
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
} as const;

// Theme Shape
export const THEME_SHAPE = {
  borderRadius: 4,
} as const;

// Theme Z-Index
export const THEME_Z_INDEX = {
  mobileStepper: 1000,
  fab: 1050,
  speedDial: 1050,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
} as const;

// Light Theme Palette
export const LIGHT_THEME_PALETTE = {
  mode: PALETTE_MODES.light,
  primary: {
    main: COLOR_CONSTANTS.primary.main,
    light: COLOR_CONSTANTS.primary.light,
    dark: COLOR_CONSTANTS.primary.dark,
    contrastText: COLOR_CONSTANTS.primary.contrastText,
  },
  secondary: {
    main: COLOR_CONSTANTS.secondary.main,
    light: COLOR_CONSTANTS.secondary.light,
    dark: COLOR_CONSTANTS.secondary.dark,
    contrastText: COLOR_CONSTANTS.secondary.contrast,
  },
  error: {
    main: COLOR_CONSTANTS.error.main,
    light: COLOR_CONSTANTS.error.light,
    dark: COLOR_CONSTANTS.error.dark,
    contrastText: COLOR_CONSTANTS.error.contrast,
  },
  warning: {
    main: COLOR_CONSTANTS.warning.main,
    light: COLOR_CONSTANTS.warning.light,
    dark: COLOR_CONSTANTS.warning.dark,
    contrastText: COLOR_CONSTANTS.warning.contrast,
  },
  info: {
    main: COLOR_CONSTANTS.info.main,
    light: COLOR_CONSTANTS.info.light,
    dark: COLOR_CONSTANTS.info.dark,
    contrastText: COLOR_CONSTANTS.info.contrast,
  },
  success: {
    main: COLOR_CONSTANTS.success.main,
    light: COLOR_CONSTANTS.success.light,
    dark: COLOR_CONSTANTS.success.dark,
    contrastText: COLOR_CONSTANTS.success.contrast,
  },
  grey: COLOR_CONSTANTS.COLOR.GREY,
  text: {
    primary: COLOR_CONSTANTS.text.primary,
    secondary: COLOR_CONSTANTS.text.secondary,
    disabled: COLOR_CONSTANTS.text.disabled,
  },
  background: {
    default: COLOR_CONSTANTS.common.white_states.main,
    paper: COLOR_CONSTANTS.common.white_states.main,
  },
  divider: COLOR_CONSTANTS.divider.divider,
} as const;

// Dark Theme Palette
export const DARK_THEME_PALETTE = {
  mode: PALETTE_MODES.dark,
  primary: {
    main: COLOR_CONSTANTS.primary.main,
    light: COLOR_CONSTANTS.primary.light,
    dark: COLOR_CONSTANTS.primary.dark,
    contrastText: COLOR_CONSTANTS.primary.contrastText,
  },
  secondary: {
    main: COLOR_CONSTANTS.secondary.main,
    light: COLOR_CONSTANTS.secondary.light,
    dark: COLOR_CONSTANTS.secondary.dark,
    contrastText: COLOR_CONSTANTS.secondary.contrast,
  },
  error: {
    main: COLOR_CONSTANTS.error.main,
    light: COLOR_CONSTANTS.error.light,
    dark: COLOR_CONSTANTS.error.dark,
    contrastText: COLOR_CONSTANTS.error.contrast,
  },
  warning: {
    main: COLOR_CONSTANTS.warning.main,
    light: COLOR_CONSTANTS.warning.light,
    dark: COLOR_CONSTANTS.warning.dark,
    contrastText: COLOR_CONSTANTS.warning.contrast,
  },
  info: {
    main: COLOR_CONSTANTS.info.main,
    light: COLOR_CONSTANTS.info.light,
    dark: COLOR_CONSTANTS.info.dark,
    contrastText: COLOR_CONSTANTS.info.contrast,
  },
  success: {
    main: COLOR_CONSTANTS.success.main,
    light: COLOR_CONSTANTS.success.light,
    dark: COLOR_CONSTANTS.success.dark,
    contrastText: COLOR_CONSTANTS.success.contrast,
  },
  grey: COLOR_CONSTANTS.COLOR.GREY,
  text: {
    primary: COLOR_CONSTANTS.common.white_states.main,
    secondary: COLOR_CONSTANTS.common.white_states.outlinedBorder,
    disabled: COLOR_CONSTANTS.common.white_states.focusVisible,
  },
  background: {
    default: COLOR_CONSTANTS.COLOR.GREY[900],
    paper: COLOR_CONSTANTS.COLOR.GREY[800],
  },
  divider: COLOR_CONSTANTS.COLOR.GREY[700],
} as const;

// Theme Typography
export const THEME_TYPOGRAPHY = {
  fontFamily: TYPOGRAPHY.fontFamilies.primary,
  fontSize: 14,
  fontWeightLight: TYPOGRAPHY.fontWeights.light,
  fontWeightRegular: TYPOGRAPHY.fontWeights.regular,
  fontWeightMedium: TYPOGRAPHY.fontWeights.medium,
  fontWeightBold: TYPOGRAPHY.fontWeights.bold,
  h1: TYPOGRAPHY.scale.h1,
  h2: TYPOGRAPHY.scale.h2,
  h3: TYPOGRAPHY.scale.h3,
  h4: TYPOGRAPHY.scale.h4,
  h5: TYPOGRAPHY.scale.h5,
  h6: TYPOGRAPHY.scale.h6,
  subtitle1: TYPOGRAPHY.scale.subtitle1,
  subtitle2: TYPOGRAPHY.scale.subtitle2,
  body1: TYPOGRAPHY.scale.body1,
  body2: TYPOGRAPHY.scale.body2,
  button: TYPOGRAPHY.scale.buttonMedium,
  caption: TYPOGRAPHY.scale.caption1,
  overline: TYPOGRAPHY.scale.overline,
} as const;

// Theme Spacing
export const THEME_SPACING = (factor: number) =>
  `${SPACING_CONSTANTS.unit * factor}px`;

// Theme Breakpoints
export const THEME_BREAKPOINTS = {
  keys: Object.keys(SPACING_CONSTANTS.breakpoints) as Array<
    keyof typeof SPACING_CONSTANTS.breakpoints
  >,
  values: SPACING_CONSTANTS.breakpoints,
  up: (key: keyof typeof SPACING_CONSTANTS.breakpoints) =>
    `@media (min-width:${SPACING_CONSTANTS.breakpoints[key]}px)`,
  down: (key: keyof typeof SPACING_CONSTANTS.breakpoints) =>
    `@media (max-width:${SPACING_CONSTANTS.breakpoints[key] - 0.05}px)`,
  between: (
    start: keyof typeof SPACING_CONSTANTS.breakpoints,
    end: keyof typeof SPACING_CONSTANTS.breakpoints
  ) =>
    `@media (min-width:${
      SPACING_CONSTANTS.breakpoints[start]
    }px) and (max-width:${SPACING_CONSTANTS.breakpoints[end] - 0.05}px)`,
  only: (key: keyof typeof SPACING_CONSTANTS.breakpoints) => {
    const keys = Object.keys(SPACING_CONSTANTS.breakpoints) as Array<
      keyof typeof SPACING_CONSTANTS.breakpoints
    >;
    const index = keys.indexOf(key);
    const nextKey = keys[index + 1];
    return nextKey
      ? `@media (min-width:${
          SPACING_CONSTANTS.breakpoints[key]
        }px) and (max-width:${SPACING_CONSTANTS.breakpoints[nextKey] - 0.05}px)`
      : `@media (min-width:${SPACING_CONSTANTS.breakpoints[key]}px)`;
  },
} as const;

// Theme Components
export const THEME_COMPONENTS = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        borderRadius: SPACING_CONSTANTS.borderRadius.md,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: SPACING_CONSTANTS.borderRadius.md,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: SPACING_CONSTANTS.borderRadius.lg,
        boxShadow: SPACING_CONSTANTS.shadows.md,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: SPACING_CONSTANTS.borderRadius.lg,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: SPACING_CONSTANTS.borderRadius.full,
      },
    },
  },
} as const;

// 통합 Theme 상수 객체
export const THEME_CONSTANTS = {
  modes: THEME_MODES,
  directions: THEME_DIRECTIONS,
  paletteModes: PALETTE_MODES,
  transitions: THEME_TRANSITIONS,
  shape: THEME_SHAPE,
  zIndex: THEME_Z_INDEX,
  lightPalette: LIGHT_THEME_PALETTE,
  darkPalette: DARK_THEME_PALETTE,
  typography: THEME_TYPOGRAPHY,
  spacing: THEME_SPACING,
  breakpoints: THEME_BREAKPOINTS,
  components: THEME_COMPONENTS,
} as const;

// 타입 정의
export type ThemeMode = keyof typeof THEME_MODES;
export type ThemeDirection = keyof typeof THEME_DIRECTIONS;
export type PaletteMode = keyof typeof PALETTE_MODES;
export type ThemeTransitionEasing = keyof typeof THEME_TRANSITIONS.easing;
export type ThemeTransitionDuration = keyof typeof THEME_TRANSITIONS.duration;
export type ThemeZIndex = keyof typeof THEME_Z_INDEX;
